/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useRef, useState } from 'react';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import { createBabylonSource } from '../scene/babylon-source';
import { grid } from '../scene/grid-scene';
import { useAuth } from '../contexts/auth-context';
import { createViewPersistence } from '../scene/view-persistence';
import type { ApiClient } from '../services/api-client';
import type { SceneEditor } from '../scene/scene-editor';
import type { SceneSource } from '../scene/types';
import type { SceneSwitcher, SwitchState } from '../scene/scene-switcher';

/**
 * What to render for a binding.
 *
 * A world with no scene yet gets the grid, which is somewhere to stand rather
 * than a scene anyone chose. A scene built in the editor is fetched on one
 * signed URL and is otherwise a self-contained `.babylon` (scene-switch.md
 * §2.8); its source id carries the revision, because publishing does not change
 * the scene id and the switcher would otherwise decide the scene already on
 * screen is the new one.
 */
const sourceFor = async (worldId: string, sceneId: string | undefined, api: ApiClient): Promise<SceneSource> => {
  if (!sceneId) {
    return grid;
  }

  const load = await api.getSceneLoad(worldId, sceneId);
  return createBabylonSource({
    id: `${sceneId}@${load.revision}`,
    name: sceneId,
    url: () => load.url,
    ...(load.bounds
      ? {
          bounds: {
            center: new Vector3(load.bounds.center.x, load.bounds.center.y, load.bounds.center.z),
            radius: load.bounds.radius,
          },
        }
      : {}),
    ...(load.default_view ? { defaultView: load.default_view } : {}),
  });
};

export interface UseSceneOptions {
  worldId: string;
  /** The world's stored binding; the editor opens on it. */
  sceneId: string | undefined;
}

export interface UseSceneResult {
  state: SwitchState;
  /** Passed to BabylonCanvas; the switcher never becomes React state. */
  onReady: (context: { editor: SceneEditor; switcher: SceneSwitcher }) => () => void;
  select: (source: SceneSource) => void;
  /** Someone else's choice, arriving over the socket. Null is an unbinding. */
  applyRemote: (sceneId: string | null) => void;
  /** Set when the choice could not be persisted, so the UI can say so. */
  saveError: Error | undefined;
}

/**
 * Bridges the imperative switcher to React and persists the choice.
 *
 * Only the switch *status* crosses into render; the switcher, editor and scene
 * graph stay outside React's lifecycle so nothing here can rebuild them.
 */
export const useScene = ({ worldId, sceneId }: UseSceneOptions): UseSceneResult => {
  const { api } = useAuth();
  const switcherRef = useRef<SceneSwitcher>(null);
  const [saveError, setSaveError] = useState<Error>();
  const [state, setState] = useState<SwitchState>({
    status: 'idle',
    activeId: undefined,
    pendingId: undefined,
    error: undefined,
  });

  /**
   * Resolving a binding can fail — a scene may have been deleted, or its URL
   * refused — and a world still has to open. The grid needs no network of any
   * kind, so it is what a failure lands on.
   */
  const open = useCallback(
    async (sceneId: string | undefined) => {
      const source = await sourceFor(worldId, sceneId, api).catch(() => grid);
      await switcherRef.current?.switchTo(source);
    },
    [api, worldId]
  );

  // Closes over sceneId rather than reading a ref: the canvas captures this
  // callback in its own mount effect, which runs before any effect here could
  // have updated one.
  const onReady = useCallback(
    ({ editor, switcher }: { editor: SceneEditor; switcher: SceneSwitcher }) => {
      switcherRef.current = switcher;
      switcher.subscribe(setState);

      // Subscribed before the first switch so the commit that follows restores
      // the viewpoint this world was left on instead of the scene's default.
      const view = createViewPersistence({ editor, switcher, worldId });

      void open(sceneId);

      return view.dispose;
    },
    [open, sceneId, worldId]
  );

  const select = useCallback(
    (source: SceneSource) => {
      void switcherRef.current?.switchTo(source);

      // Persisted after the switch starts: the viewport should not wait on a
      // round trip, and a failed write leaves the scene on screen with a notice
      // rather than reverting under the user.
      api.setWorldScene(worldId, { scene_id: source.id }).then(
        () => setSaveError(undefined),
        (error: Error) => setSaveError(error)
      );
    },
    [api, worldId]
  );

  // Not persisted: the tab that made the change already wrote it, and writing
  // it again from every listener would be N round trips per switch. Switching
  // to the scene already showing is a no-op, so an echo costs nothing.
  const applyRemote = useCallback((sceneId: string | null) => void open(sceneId ?? undefined), [open]);

  return { state, onReady, select, applyRemote, saveError };
};
